import { useState, useEffect, ChangeEvent } from 'react';
import { Slider as FplSlider } from '@figma/fpl-components';
import { Box } from '@/UI/Box/Box';
import { Input } from '@/UI/Input/Input';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { StyledSlider, FullWidthBox, Row } from './number-input-field.styled';

// A fixed 0.1 step makes ranges narrower than ~1 unusable (e.g. min 0 / max 0.1
// gives a single intermediate stop). Derive the step from the range — targeting
// ~100 increments, snapped to a power of ten so the values stay readable — and
// cap it at 0.1 so wider sliders keep their existing resolution.
const floatStep = (min: number, max: number): number => {
  const span = Math.abs(max - min);
  if (!Number.isFinite(span) || span <= 0) {
    return 0.1;
  }
  return Math.min(0.1, 10 ** Math.floor(Math.log10(span / 100)));
};

export const NumberInputField = ({
  inputKey,
  title,
  value,
  disabled,
  min,
  max,
  type,
  step: stepOverride,
  onChange,
}: NumberInputFieldProps): React.JSX.Element => {
  const isFplEnabled = useFplMigrationFlag();
  const [tempValue, setTempValue] = useState(`${value || 0}`);
  const step = stepOverride ?? (type === 'integer' ? 1 : floatStep(min, max));

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => setTempValue(e.target.value); // Update temporary value

  useEffect(() => {
    setTempValue(`${value || 0}`);
  }, [value]);

  const submitNewValue = () => {
    let newValue = parseFloat(tempValue);
    if (isNaN(newValue)) {
      newValue = value;
    }
    if (newValue <= min) {
      newValue = min;
    }
    if (newValue >= max) {
      newValue = max;
    }
    if (type === 'integer') {
      newValue = Math.round(newValue);
    } else if (stepOverride) {
      // The slider already snaps; a typed value has to be snapped too, without float dust.
      newValue = Number((Math.round(newValue / stepOverride) * stepOverride).toFixed(4));
    }

    setTempValue(String(newValue));
    onChange(inputKey, newValue, false);
  };

  return (
    <FullWidthBox>
      <Row>
        {isFplEnabled ? (
          <Box sx={{ flex: 1, minWidth: 0, mr: 2 }}>
            <FplSlider
              disabled={disabled}
              value={value || 0}
              onChange={(next, { commit }) => {
                setTempValue(String(next));
                onChange(inputKey, next, !commit);
              }}
              aria-label={title || inputKey}
              min={min}
              max={max}
              step={step}
              bigStep={step * 10}
              rangeAnchor={min}
              size="sm"
            />
          </Box>
        ) : (
          <StyledSlider
            disabled={disabled}
            valueLabelDisplay="auto"
            value={value || 0}
            onChange={(_, newValue) => {
              const next = Array.isArray(newValue) ? newValue[0] : newValue;
              setTempValue(String(next));
              onChange(inputKey, next, true);
            }}
            onChangeCommitted={(_, newValue) => {
              onChange(inputKey, Array.isArray(newValue) ? newValue[0] : newValue, false);
            }}
            aria-labelledby="input-slider"
            min={min}
            max={max}
            step={step}
            size="small"
          />
        )}
        <Input
          sx={{ width: '52px', flexShrink: 0, '& .MuiInputBase-root': { height: '24px', px: 0.5 } }}
          disabled={disabled}
          value={tempValue}
          size="small"
          onChange={handleInputChange}
          onBlur={submitNewValue}
          onFocus={(e) => e.target.select()}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              submitNewValue();
            }
          }}
          inputProps={{
            step,
            min,
            max,
            type: 'number',
            'aria-label': title || inputKey,
          }}
        />
      </Row>
    </FullWidthBox>
  );
};
